import React, { memo, useContext, useEffect, useMemo } from 'react';

import { SearchOutlined } from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InputAdornment } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { useAssetTypesWithAssetsQuery } from '../../queries/assets';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setRightColumn } from '../../store/modules/listManager.slice';
import { cleanObjects, getObjects, setObjects } from '../../store/modules/listObject.slice';
import { getLocations } from '../../store/modules/locations.slice';
import { SkipLogicQuestion } from '../../types/formBuilder';
import { paths } from '../../utils/navigation';
import { Ckeditor } from '../uiComponents/ckeditor';
import { InfoAlert } from '../uiComponents/infoAlert';
import { ActivityPeriodTimeSetting } from './activityPeriodTimeSetting';
import { normalizeMeterConfigurations } from './assetsUtils/meterConfigurationsUtils';
import { buildCustomAssetList } from './buildCustomAssetList';
import colors from './colors';
import { DateValidation } from './dateValidation/dateValidation';
import { DateQuestionField } from './dateValidation/types';
import { FileSetting } from './fileSetting';
import { FormBuilderContext } from './FormBuilderProvider';
import { ALL_METADATA_TYPES } from './helper';
import { MeterSetting } from './meterSetting';
import { NumberValidation } from './numberValidation';
import { OldLocationsWarningBanner } from './OldLocationsWarningBanner';
import { PhotoSettings } from './photoSettings';
import { questionTypesList } from './questionTypes';

interface StyleSheet {
  [key: string]: React.CSSProperties;
}

const TableQuestionBuilder = memo(
  (props: {
    tableId: string;
    index: number;
    question: any;
    dataSources: { tag: string; value: string }[];
    paying: boolean;
    logicQuestions: SkipLogicQuestion[];
    onNameInTableChange: (id: string, value: string, tableId: string) => void;
    onDescriptionInTableChange: (id: string, value: string, tableId: string) => void;
    onStaticTextInTableChange: (id: string, value: string, tableId: string) => void;
    onDateModeInTableChange: (id: string, value: string, tableId: string) => void;
    onOptionInTableChange: (
      id: string,
      index: number,
      action: string,
      value: string,
      tableId: string,
    ) => void;
    onListChoicesInTableChange: (
      id: string,
      index: number,
      action: string,
      value: string,
      tableId: string,
    ) => void;
    qualityCamOnChange: (id: string, value: string, tableId: string) => void;
    onFileSettingChange: (
      id: string,
      value: number | string[],
      action: string,
      tableId: string,
    ) => void;
    onActivityPeriodTimeSettingChange: (
      id: string,
      value: string,
      action: string,
      tableId: string,
    ) => void;
    onRichTextChange: (id: string, value: string, tableId: string) => void;
    onRelatedListInTableChange: (
      id: string,
      action: string,
      value: string,
      tableId: string,
    ) => void;
    onNumberValidationTableChange: (
      id: string,
      action: string,
      value: string,
      tableId: string,
    ) => void;
    onDateValidationTableChange: (
      id: string,
      dateQuestionFieldToUpdate: DateQuestionField,
      value: string,
      tableId: string,
    ) => void;
    onDateValidationTableBatchChange?: (
      questionIdInTable: string,
      dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
      tableId: string,
    ) => void;
    onAddMeterConfiguration: (questionId: string, tableId?: string) => void;
    onDeleteMeterConfiguration: (questionId: string, configIndex: number, tableId?: string) => void;
    onMeterConfigurationMeterChange: (
      questionId: string,
      configIndex: number,
      newMeterId: string,
      tableId?: string,
    ) => void;
    onAddMeterConfigurationQuestion: (
      questionId: string,
      configIndex: number,
      tableId?: string,
    ) => void;
    onUpdateMeterConfigurationQuestion: (
      questionId: string,
      configIndex: number,
      questionSlotIndex: number,
      value: string,
      tableId?: string,
    ) => void;
    onDeleteMeterConfigurationQuestion: (
      questionId: string,
      configIndex: number,
      questionSlotIndex: number,
      tableId?: string,
    ) => void;
    onCreateAssetQuestion: (
      index: number,
      tableId: string | undefined,
      type: 'number' | 'select_metadata',
    ) => void;
  }) => {
    const {
      tableId,
      question: {
        index,
        id,
        name,
        description,
        static_text,
        type,
        dateMode,
        dateValidationType,
        rangeCondition,
        offsetHours,
        offsetHoursToLeft,
        offsetHoursToRight,
        options,
        metadata,
        nestableLocations,
        selectedAssets,
        listChoices,
        cameraQuality,
        formats,
        quantity,
        maximumAudioDurationInSeconds,
        startTime,
        workingTime,
        intervalDefault,
        rich_text,
        related_list,
        list_header,
        separator,
        upperValue,
        lowerValue,
        numberValidation,
        numberValidationRelative,
        validationOffset,
        updateData,
        meterConfigurations,
      },
      dataSources,
      paying,
      logicQuestions,
      onNameInTableChange,
      onDescriptionInTableChange,
      onStaticTextInTableChange,
      onDateModeInTableChange,
      onOptionInTableChange,
      onListChoicesInTableChange,
      qualityCamOnChange,
      onFileSettingChange,
      onActivityPeriodTimeSettingChange,
      onRichTextChange,
      onRelatedListInTableChange,
      onNumberValidationTableChange,
      onDateValidationTableChange,
      onDateValidationTableBatchChange,
      onAddMeterConfiguration,
      onDeleteMeterConfiguration,
      onMeterConfigurationMeterChange,
      onAddMeterConfigurationQuestion,
      onUpdateMeterConfigurationQuestion,
      onDeleteMeterConfigurationQuestion,
      onCreateAssetQuestion,
    } = props;

    const lang = useLanguage();
    const { checkFlag } = useFeatureFlags();
    const dateQuestionValidationFlag = checkFlag('DateQuestionValidation');

    const styles: StyleSheet = {
      mainContainer: {
        display: 'flex',
        flexDirection: 'column',
      },
      warningContainer: {
        alignSelf: 'center',
        backgroundColor: colors.primary,
        color: 'white',
        borderRadius: 9,
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 'bold',
        paddingInline: 5,
        marginBottom: 7,
      },
      mobileOnlyAlert: {
        marginBottom: '24px',
      },
    };
    const dispatch = useAppDispatch();
    const {
      isActive: listManagerAllowed,
      showPanel,
      rightColumn,
    } = useAppSelector((state) => state.listManager);
    const listsRequest = async (code: string) => {
      try {
        window.open(
          `${window.location.origin}${paths.lists.index}?metadata_code=${code}`,
          '_blank',
        );
      } catch (error) {
        console.log(error);
      }
    };

    const { data: assetTypesData } = useAssetTypesWithAssetsQuery('');
    const hasCustomAssetSelection =
      (selectedAssets?.assets?.length ?? 0) > 0 || (selectedAssets?.asset_types?.length ?? 0) > 0;

    useEffect(() => {
      if (listManagerAllowed && metadata && type !== 'table') {
        if (metadata === 'locations') {
          dispatch(getLocations({}));
          dispatch(setRightColumn('LOCATIONS'));
        } else if (metadata === 'assets') {
          const assetTypesCount = assetTypesData?.asset_types_with_assets
            ? Object.keys(assetTypesData.asset_types_with_assets).length
            : 0;
          const allAssetTypesSelected =
            assetTypesCount > 0 &&
            (selectedAssets?.asset_types?.length ?? 0) === assetTypesCount &&
            (selectedAssets?.assets?.length ?? 0) === 0;
          if (!hasCustomAssetSelection || allAssetTypesSelected) {
            dispatch(getObjects({ metadataCode: 'assets' }));
          } else if (assetTypesData?.asset_types_with_assets) {
            const objects = buildCustomAssetList(
              assetTypesData.asset_types_with_assets,
              selectedAssets ?? {},
            );
            dispatch(setObjects({ objects }));
          } else {
            dispatch(getObjects({ metadataCode: 'assets' }));
          }
          dispatch(setRightColumn('ASSETS'));
        } else if (metadata === 'nestable_locations') {
          dispatch(setRightColumn(null));
        } else {
          dispatch(getObjects({ metadataCode: metadata }));
          dispatch(setRightColumn('OBJECTS'));
        }
      } else {
        dispatch(setRightColumn(''));
        dispatch(cleanObjects());
      }
    }, [
      dispatch,
      listManagerAllowed,
      metadata,
      showPanel,
      type,
      rightColumn,
      hasCustomAssetSelection,
      selectedAssets,
      assetTypesData?.asset_types_with_assets,
    ]);

    const { setSearchDataListModalOpen, setListModalQuestionId, setListModalTableId } =
      useContext(FormBuilderContext);

    const dataSourceLabel = useMemo(() => {
      if (nestableLocations) {
        return lang.form_builder.customLocations;
      }
      return dataSources.find((source) => source.value === metadata)?.tag ?? dataSources[0].tag;
    }, [metadata, dataSources, nestableLocations]);

    return (
      <div style={styles.mainContainer}>
        {!questionTypesList.includes(type) && (
          <div style={{ ...styles.warningContainer, backgroundColor: colors.red }}>
            {lang.form_builder.deprecatedWarning}
          </div>
        )}
        <OldLocationsWarningBanner selectedMetadata={metadata} />
        <div style={{ width: '65%' }}>
          <TextField
            variant={'outlined'}
            size={'small'}
            margin={'dense'}
            label={lang.form_builder.title}
            value={name}
            onChange={(e) => onNameInTableChange(id, e.target.value, tableId)}
            fullWidth
            required
            error={name ? false : true}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <Tooltip
                    title={lang.form_builder.titleDescription}
                    placement={'top'}
                  >
                    <InfoOutlinedIcon style={{ fontSize: 14 }} />
                  </Tooltip>
                </InputAdornment>
              ),
              style: {
                fontSize: 12,
              },
            }}
            InputLabelProps={{
              style: {
                fontSize: 14,
              },
            }}
            inputProps={{
              maxLength: 255,
            }}
          />
        </div>

        {type === 'static_text' ? (
          <>
            <div>
              {static_text ? (
                <TextField
                  variant={'outlined'}
                  size={'small'}
                  margin={'dense'}
                  label={lang.form_builder.instruction}
                  value={static_text}
                  onChange={(e) => onStaticTextInTableChange(id, e.target.value, tableId)}
                  fullWidth
                  multiline
                  rows={4}
                  InputProps={{
                    style: {
                      fontSize: 12,
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      fontSize: 14,
                    },
                  }}
                  inputProps={{
                    maxLength: 1024,
                  }}
                />
              ) : (
                <Ckeditor
                  value={rich_text}
                  onChange={(value: string) => onRichTextChange(id, value, tableId)}
                />
              )}
            </div>
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        ) : (
          <>
            <div>
              <TextField
                variant={'outlined'}
                size={'small'}
                margin={'dense'}
                label={lang.form_builder.instruction}
                value={description}
                onChange={(e) => onDescriptionInTableChange(id, e.target.value, tableId)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Tooltip
                        title={lang.form_builder.instructionDescription}
                        placement={'top'}
                      >
                        <InfoOutlinedIcon style={{ fontSize: 14 }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                  style: {
                    fontSize: 12,
                  },
                }}
                InputLabelProps={{
                  style: {
                    fontSize: 14,
                  },
                }}
                inputProps={{
                  maxLength: 1024,
                }}
              />
            </div>
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        )}
        {type === 'number' && (
          <>
            <NumberValidation
              questionId={id}
              upperValue={upperValue}
              lowerValue={lowerValue}
              numberValidation={numberValidation}
              onNumberValidationChange={onNumberValidationTableChange}
              tableId={tableId}
              numberValidationRelative={numberValidationRelative}
              validationOffset={validationOffset}
              logicQuestions={logicQuestions}
            />{' '}
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        )}
        {type === 'attachment' && (
          <>
            <FileSetting
              questionId={id}
              formats={formats}
              onFileSettingChange={onFileSettingChange}
              quantity={quantity}
              maximumAudioDurationInSeconds={maximumAudioDurationInSeconds}
              tableId={tableId}
            />
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        )}

        {type === 'date' && (
          <>
            <div>
              <FormLabel
                style={{ fontSize: 12, marginBottom: 7 }}
                component='legend'
              >
                {lang.form_builder.type}
              </FormLabel>
              <RadioGroup
                value={dateMode}
                onChange={(e) => onDateModeInTableChange(id, e.target.value, tableId)}
              >
                <FormControlLabel
                  value='date'
                  control={
                    <Radio
                      size={'small'}
                      style={{ padding: '5px 15px' }}
                      color={'primary'}
                    />
                  }
                  label={<div style={{ fontSize: 12 }}>{lang.form_builder.date}</div>}
                />
                <FormControlLabel
                  value='datetime'
                  control={
                    <Radio
                      size={'small'}
                      style={{ padding: '5px 15px' }}
                      color={'primary'}
                    />
                  }
                  label={<div style={{ fontSize: 12 }}>{lang.form_builder.dateAndTime}</div>}
                />
                <FormControlLabel
                  value='time'
                  control={
                    <Radio
                      size={'small'}
                      style={{ padding: '5px 15px' }}
                      color={'primary'}
                    />
                  }
                  label={<div style={{ fontSize: 12 }}>{lang.form_builder.time}</div>}
                />
              </RadioGroup>
            </div>
            <Divider style={{ margin: '15px 0 15px' }} />
            {dateQuestionValidationFlag && (
              <DateValidation
                questionId={id}
                dateMode={dateMode}
                dateValidationType={dateValidationType}
                rangeCondition={rangeCondition}
                offsetHours={offsetHours}
                offsetHoursToLeft={offsetHoursToLeft}
                offsetHoursToRight={offsetHoursToRight}
                onDateValidationChange={onDateValidationTableChange}
                onDateValidationTableBatchChange={onDateValidationTableBatchChange}
                tableId={tableId}
              />
            )}
          </>
        )}

        {type === 'barcode' && (
          <div style={styles.mobileOnlyAlert}>
            <InfoAlert message={lang.form_builder.mobileOnlyAlert} />
          </div>
        )}

        {ALL_METADATA_TYPES.includes(type) && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <FormLabel
              style={{
                fontSize: 12,
                textAlign: 'center',
              }}
              component='legend'
            >
              {lang.form_builder.dataListSource}
            </FormLabel>
            <Button
              sx={{ borderRadius: '2px', borderBottom: '1px solid', fontSize: 12 }}
              size={'small'}
              endIcon={
                <InputAdornment position='start'>
                  <SearchOutlined
                    fontSize='inherit'
                    color='primary'
                  />
                </InputAdornment>
              }
              onClick={() => {
                setListModalQuestionId(id);
                setListModalTableId(tableId);
                setSearchDataListModalOpen(true);
              }}
            >
              {dataSourceLabel}
            </Button>
          </div>
        )}
        {related_list && ['multi_option', 'select_metadata'].includes(type) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              columnGap: 5,
              marginBottom: 5,
            }}
          >
            <div style={{ flexGrow: 1 }}>
              <FormLabel
                style={{
                  fontSize: 12,
                  textAlign: 'left',
                }}
                component='legend'
              >
                {lang.form_builder.header}
              </FormLabel>
              <TextField
                variant={'outlined'}
                size={'small'}
                margin={'dense'}
                placeholder={lang.form_builder.headerPlaceholder}
                value={list_header}
                error={!list_header?.trim()?.includes(separator)}
                helperText={
                  !list_header?.trim()?.includes(separator) ? lang.form_builder.headerWarning : ''
                }
                onChange={(e) =>
                  onRelatedListInTableChange(id, 'list_header', e.target.value, tableId)
                }
                onBlur={() => {
                  if (!list_header?.trim())
                    onRelatedListInTableChange(
                      id,
                      'list_header',
                      lang.form_builder.headerDefault,
                      tableId,
                    );
                }}
                fullWidth
                InputProps={{
                  style: {
                    fontSize: 12,
                  },
                }}
              />
            </div>
            <div>
              <FormLabel
                style={{
                  fontSize: 12,
                  textAlign: 'left',
                }}
                component='legend'
              >
                {lang.form_builder.separator}
              </FormLabel>
              <TextField
                variant={'outlined'}
                size={'small'}
                value={separator}
                error={!separator?.trim()}
                helperText={!separator?.trim() ? lang.form_builder.separatorWarning : ''}
                margin={'dense'}
                style={{ width: 80 }}
                InputProps={{
                  style: {
                    fontSize: 12,
                  },
                }}
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: 'center' },
                }}
                FormHelperTextProps={{ style: { fontSize: 10, margin: 5 } }}
                onChange={(e) =>
                  onRelatedListInTableChange(id, 'separator', e.target.value, tableId)
                }
                onBlur={() => {
                  if (!separator?.trim()) onRelatedListInTableChange(id, 'separator', '|', tableId);
                }}
              />
            </div>
          </div>
        )}
        {ALL_METADATA_TYPES.includes(type) && !nestableLocations && (
          <>
            <div>
              {metadata ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 10,
                  }}
                >
                  <Button
                    onClick={() => listsRequest(metadata)}
                    variant='outlined'
                    size={'small'}
                    style={{
                      backgroundColor: colors.primary,
                      color: 'white',
                      height: 30,
                    }}
                  >
                    <div style={{ textTransform: 'capitalize' }}>
                      {lang.form_builder.changeDataList}
                    </div>
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 10, alignItems: 'center' }}>
                  <FormLabel
                    style={{
                      fontSize: 12,
                      textAlign: 'center',
                    }}
                    component='legend'
                  >
                    {lang.form_builder.listValues}
                  </FormLabel>

                  {listChoices && (
                    <div
                      style={{
                        flexDirection: 'column',
                      }}
                    >
                      {listChoices.map((choice: string[], index: number) => (
                        <div
                          key={`table-question-builder-choice-${index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <TextField
                            variant={'outlined'}
                            size={'small'}
                            margin={'dense'}
                            placeholder={`Alternativa ${index + 1}`}
                            value={choice}
                            onChange={(e) =>
                              onListChoicesInTableChange(
                                id,
                                index,
                                'update',
                                e.target.value,
                                tableId,
                              )
                            }
                            fullWidth
                            InputProps={{
                              style: {
                                fontSize: 12,
                              },
                            }}
                            inputProps={{
                              maxLength: 1024,
                            }}
                          />

                          <div
                            style={{ cursor: 'pointer', marginLeft: 10 }}
                            onClick={() =>
                              onListChoicesInTableChange(id, index, 'delete', 'default', tableId)
                            }
                          >
                            <Tooltip
                              title={lang.form_builder.deleteAlternative}
                              placement={'right-end'}
                            >
                              <CloseRoundedIcon
                                fontSize={'small'}
                                style={{
                                  fontSize: 17,
                                  color: colors.darkGrey,
                                  paddingTop: 9,
                                }}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    style={{
                      margin: 5,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        onListChoicesInTableChange(
                          id,
                          -1,
                          'create',
                          `${lang.form_builder.alternative} ${
                            listChoices ? listChoices.length + 1 : 1
                          }`,
                          tableId,
                        )
                      }
                    >
                      <AddRoundedIcon style={{ fontSize: 20, color: '#2196f3' }} />
                      <FormLabel
                        style={{
                          fontSize: 12,
                          textAlign: 'center',
                          color: '#2196f3',
                        }}
                        component='legend'
                      >
                        {lang.form_builder.addListValue}
                      </FormLabel>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        )}
        {type === 'select_activity_period_time' && (
          <>
            <ActivityPeriodTimeSetting
              questionId={id}
              tableId={tableId}
              onActivityPeriodTimeSettingChange={onActivityPeriodTimeSettingChange}
              startTime={startTime}
              workingTime={workingTime}
              intervalDefault={intervalDefault}
            />
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        )}
        {type === 'select_metadata' &&
          rightColumn === 'ASSETS' &&
          (Boolean(meterConfigurations?.length) || updateData === '1') && (
            <>
              <MeterSetting
                questionIndex={index}
                questionId={id}
                meterConfigurations={normalizeMeterConfigurations(props.question)}
                tableId={tableId}
                onAddMeterConfiguration={onAddMeterConfiguration}
                onDeleteMeterConfiguration={onDeleteMeterConfiguration}
                onMeterConfigurationMeterChange={onMeterConfigurationMeterChange}
                onAddMeterConfigurationQuestion={onAddMeterConfigurationQuestion}
                onUpdateMeterConfigurationQuestion={onUpdateMeterConfigurationQuestion}
                onDeleteMeterConfigurationQuestion={onDeleteMeterConfigurationQuestion}
                selectedAssets={selectedAssets}
                onCreateAssetQuestion={onCreateAssetQuestion}
              />
              <Divider style={{ margin: '15px 0 15px' }} />
            </>
          )}
        {['multi_option', 'select_option_metadata', 'select_option_metadata_comments'].includes(
          type,
        ) && (
          <>
            <div style={{ alignItems: 'center', marginTop: 10 }}>
              <FormLabel
                style={{
                  fontSize: 12,
                  textAlign: 'center',
                }}
                component='legend'
              >
                {lang.form_builder.alternativeColumns}
              </FormLabel>
              <div
                style={{
                  flexDirection: 'column',
                }}
              >
                {options &&
                  options.map((option: any, index: number) => (
                    <div
                      key={`table-question-builder-option-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <TextField
                        variant={'outlined'}
                        size={'small'}
                        margin={'dense'}
                        placeholder={`Alternativa ${index + 1}`}
                        value={type === 'select_option_metadata_comments' ? option.option : option}
                        onChange={(e) =>
                          onOptionInTableChange(id, index, 'update', e.target.value, tableId)
                        }
                        fullWidth
                        InputProps={{
                          style: {
                            fontSize: 12,
                          },
                        }}
                        inputProps={{
                          maxLength: 1024,
                        }}
                      />
                      <div
                        style={{ cursor: 'pointer', marginLeft: 10 }}
                        onClick={() =>
                          onOptionInTableChange(id, index, 'delete', 'default', tableId)
                        }
                      >
                        <Tooltip
                          title={lang.form_builder.deleteAlternative}
                          placement={'right-end'}
                        >
                          <CloseRoundedIcon
                            fontSize={'small'}
                            style={{
                              fontSize: 17,
                              color: colors.darkGrey,
                              paddingTop: 9,
                            }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ))}
              </div>
              <div
                style={{
                  margin: 5,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    onOptionInTableChange(id, -1, 'create', lang.form_builder.newOption, tableId)
                  }
                >
                  <AddRoundedIcon style={{ fontSize: 20, color: '#2196f3' }} />
                  <FormLabel
                    style={{
                      fontSize: 12,
                      textAlign: 'center',
                      color: '#2196f3',
                    }}
                    component='legend'
                  >
                    {lang.form_builder.addOption}
                  </FormLabel>
                </div>
              </div>
            </div>
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        )}
        {(type === 'photo' || type === 'multi_photo') && (
          <>
            <PhotoSettings
              tableId={tableId}
              questionId={id}
              paying={paying}
              cameraQuality={cameraQuality}
              qualityCamOnChange={qualityCamOnChange}
            />{' '}
            <Divider style={{ margin: '15px 0 15px' }} />
          </>
        )}
      </div>
    );
  },
);

export { TableQuestionBuilder };
